import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  useAuth,
  IDLE_TIMEOUT_MINUTES,
  IDLE_WARNING_MINUTES,
  touchActivity,
  resetActivity,
  idleMinutes,
  hasActivityStamp,
} from '../contexts/AuthContext';
import { AlertTriangle, Clock } from 'lucide-react';

// ── HIPAA automatic logoff, 45 CFR § 164.312(a)(2)(iii) ────────────────────
// 15 minutes of inactivity → logout for ALL roles, with a warning at 13
// (15 = the retired in-memory timer's long-standing value; this version
// hardens it: persistent across remounts/refreshes, Teams-aware).
// Driven by the persisted last-activity timestamp in AuthContext (framed =
// localStorage, top-level = sessionStorage — same store as the token), so a
// Teams iframe remount neither resets nor loses the idle clock. The 8-hour
// ABSOLUTE JWT expiry (ACCESS_TOKEN_EXPIRE_MINUTES=480) is unchanged.
// Per-role timeout values are a future one-line change to the constants.
const CHECK_INTERVAL_MS = 30 * 1000; // check idle state roughly every 30s
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'mousemove'];

export default function SessionTimeoutManager({ children }) {
  const { user, logout } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(
    (IDLE_TIMEOUT_MINUTES - IDLE_WARNING_MINUTES) * 60
  );
  const showWarningRef = useRef(false);
  showWarningRef.current = showWarning;

  // 60-minute timeout reuses the shared clearAuthStorage path (via logout)
  // and the same /login destination the 401 interceptor uses.
  const handleTimeout = useCallback(() => {
    logout();
    sessionStorage.setItem('session_timeout', 'true');
    window.location.href = '/login?timeout=true';
  }, [logout]);

  const staySignedIn = useCallback(() => {
    resetActivity();
    setShowWarning(false);
  }, []);

  // Idle check on mount + every 30s. The mount check is READ-ONLY: an
  // iframe remount must not itself count as activity. The one exception is
  // first-run migration — a session authenticated before this feature has
  // no stamp yet, so we seed one exactly once.
  useEffect(() => {
    if (!user) return;
    if (!hasActivityStamp()) resetActivity();
    const check = () => {
      const idle = idleMinutes();
      if (idle >= IDLE_TIMEOUT_MINUTES) {
        handleTimeout();
        return;
      }
      if (idle >= IDLE_WARNING_MINUTES) {
        setRemainingSeconds(Math.max(0, Math.round((IDLE_TIMEOUT_MINUTES - idle) * 60)));
        setShowWarning(true);
      }
    };
    check();
    const id = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [user, handleTimeout]);

  // 1s countdown while the warning is visible; reaching 0 logs out.
  useEffect(() => {
    if (!showWarning) return;
    const id = setInterval(() => {
      const left = (IDLE_TIMEOUT_MINUTES - idleMinutes()) * 60;
      if (left <= 0) {
        handleTimeout();
        return;
      }
      setRemainingSeconds(Math.round(left));
    }, 1000);
    return () => clearInterval(id);
  }, [showWarning, handleTimeout]);

  // Activity listeners: persisted timestamp writes are throttled to ≤1 per
  // 30s inside touchActivity. Any activity during the warning period resets
  // the idle clock and dismisses the warning (once-per-idle-period).
  useEffect(() => {
    if (!user) return;
    const handleActivity = () => {
      touchActivity();
      if (showWarningRef.current) {
        resetActivity();
        setShowWarning(false);
      }
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, handleActivity, { passive: true }));
    return () => ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, handleActivity));
  }, [user]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <>
      {children}

      {showWarning && user && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999]"
          data-testid="session-timeout-warning"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
            <div className="bg-amber-50 border-b border-amber-100 px-6 py-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-slate-900">Still there?</h2>
                  <p className="text-sm text-slate-500">
                    You'll be signed out soon due to inactivity
                  </p>
                </div>
              </div>
            </div>

            <div className="px-6 py-6 text-center">
              <div
                className="flex items-center justify-center gap-2 text-3xl font-bold text-slate-900 mb-4"
                data-testid="session-timeout-countdown"
              >
                <Clock className="w-8 h-8 text-amber-500" />
                <span>{formatTime(remainingSeconds)}</span>
              </div>
              <p className="text-slate-600 mb-2">
                For your security, you'll be automatically signed out after{' '}
                {IDLE_TIMEOUT_MINUTES} minutes of inactivity.
              </p>
              <p className="text-sm text-slate-500">
                Click "Stay signed in" to continue your session.
              </p>
            </div>

            <div className="px-6 py-4 bg-slate-50 border-t border-slate-100 flex gap-3">
              <button
                onClick={handleTimeout}
                className="flex-1 px-4 py-2.5 border border-slate-200 rounded-lg text-slate-600 font-medium hover:bg-slate-100 transition-colors"
                data-testid="logout-now-btn"
              >
                Sign out now
              </button>
              <button
                onClick={staySignedIn}
                className="flex-1 px-4 py-2.5 bg-emerald-600 text-white rounded-lg font-medium hover:bg-emerald-700 transition-colors"
                data-testid="stay-logged-in-btn"
              >
                Stay signed in
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
